import React, { useState, useRef, useEffect, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useAccount } from "../contexts/AccountContext";

interface ProposedAction {
  type: string;
  params: Record<string, unknown>;
  summary: string;
  callable: string;
  mutation: boolean;
}
interface Msg {
  role: "user" | "assistant";
  content: string;
  action?: ProposedAction;
  escalated?: boolean;
  actionState?: "idle" | "running" | "done" | "error" | "cancelled";
  actionResult?: string;
}

/**
 * Floating support chat bubble for account admins. Persists to supportSendMessage
 * (server-side). When the assistant proposes an action it renders a Confirm
 * button that calls the named account-scoped callable — which re-checks
 * permissions server-side, so the assistant can never exceed the admin's own
 * account rights. Escalations are handled and emailed server-side.
 */
const SupportChatBubble: React.FC = () => {
  const { isAdmin, claims } = useAuth();
  const { account } = useAccount();
  const accountId = account?.id;

  // Show for anyone who is an owner/admin — either by active role (isAdmin) or by
  // their underlying claim on this account — so switching to a supervisor view
  // doesn't hide support. Robust against the active-role toggle.
  const accountRoles: string[] = accountId
    ? ((claims as any)?.accounts?.[accountId]?.roles as string[] | undefined) || []
    : [];
  const canUseSupport = isAdmin || accountRoles.includes("owner") || accountRoles.includes("admin");

  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | undefined>();
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    { role: "assistant", content: "Hi! I'm the VisitProof assistant. Ask me anything, or tell me what you need help with — I can fix team access, invite members, help schedule visits, and more." },
  ]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || !accountId || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const fn = httpsCallable(functions, "supportSendMessage");
      const res: any = await fn({ accountId, threadId, text });
      setThreadId(res.data.threadId);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: res.data.reply,
          action: res.data.action,
          escalated: res.data.escalated,
          actionState: "idle",
        },
      ]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — something went wrong. Please try again." }]);
    } finally {
      setSending(false);
    }
  }, [input, accountId, threadId, sending]);

  const runAction = useCallback(
    async (idx: number, action: ProposedAction) => {
      if (!accountId) return;
      setMessages((m) => m.map((msg, i) => (i === idx ? { ...msg, actionState: "running" } : msg)));
      try {
        const fn = httpsCallable(functions, action.callable);
        // Account-scoped callables all take accountId; pass along the model's params.
        await fn({ accountId, ...action.params });
        setMessages((m) =>
          m.map((msg, i) =>
            i === idx ? { ...msg, actionState: "done", actionResult: "Done ✓ — if it affects a login, ask them to sign out and back in." } : msg
          )
        );
      } catch (e: any) {
        setMessages((m) =>
          m.map((msg, i) => (i === idx ? { ...msg, actionState: "error", actionResult: e?.message || "That action failed." } : msg))
        );
      }
    },
    [accountId]
  );

  const cancelAction = useCallback((idx: number) => {
    setMessages((m) =>
      m.map((msg, i) => (i === idx ? { ...msg, actionState: "cancelled", actionResult: "Cancelled — nothing was changed." } : msg))
    );
  }, []);

  // Only account owners/admins get the assistant.
  if (!canUseSupport || !accountId) return null;

  return (
    <>
      {/* Bubble */}
      <button
        aria-label="Support"
        onClick={() => setOpen((o) => !o)}
        style={{
          position: "fixed", right: 20, bottom: 20, zIndex: 50,
          width: 56, height: 56, borderRadius: 28, border: "none",
          background: "#0066cc", color: "#fff", cursor: "pointer",
          boxShadow: "0 4px 14px rgba(0,0,0,0.25)", fontSize: 24,
        }}
      >
        {open ? "×" : "💬"}
      </button>

      {open && (
        <div
          style={{
            position: "fixed", right: 20, bottom: 88, zIndex: 50,
            width: 360, maxWidth: "calc(100vw - 40px)", height: 520, maxHeight: "calc(100vh - 120px)",
            background: "#fff", borderRadius: 12, boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}
          className="dark:bg-zinc-900"
        >
          <div style={{ background: "#0066cc", color: "#fff", padding: "12px 16px", fontWeight: 600 }}>
            VisitProof Support
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 12, background: "#f7f8fa" }} className="dark:bg-zinc-800">
            {messages.map((m, i) => (
              <div key={i} style={{ marginBottom: 10, textAlign: m.role === "user" ? "right" : "left" }}>
                <div
                  style={{
                    display: "inline-block", padding: "8px 12px", borderRadius: 12, maxWidth: "85%",
                    background: m.role === "user" ? "#0066cc" : "#fff",
                    color: m.role === "user" ? "#fff" : "#111",
                    border: m.role === "user" ? "none" : "1px solid #e5e7eb",
                    fontSize: 14, textAlign: "left", whiteSpace: "pre-wrap",
                  }}
                >
                  {m.content}
                </div>

                {m.escalated && (
                  <div style={{ marginTop: 4, fontSize: 12, color: "#b45309" }}>
                    Passed to our team — they'll follow up by email.
                  </div>
                )}

                {m.action && m.actionState !== "done" && m.actionState !== "cancelled" && (
                  <div
                    style={{
                      marginTop: 8,
                      border: "1px solid #d7dbe0",
                      borderRadius: 12,
                      background: "#fff",
                      padding: 12,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#6b7280", marginBottom: 4 }}>
                      Action to confirm
                    </div>
                    <div style={{ fontSize: 14, color: "#111", marginBottom: 10 }}>{m.action.summary}</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => runAction(i, m.action!)}
                        disabled={m.actionState === "running"}
                        style={{
                          flex: 1, padding: "9px 12px", borderRadius: 8, border: "none",
                          background: m.actionState === "running" ? "#7fb0e0" : "#0066cc",
                          color: "#fff", cursor: m.actionState === "running" ? "default" : "pointer",
                          fontSize: 14, fontWeight: 600,
                        }}
                      >
                        {m.actionState === "running" ? "Working…" : "Confirm"}
                      </button>
                      <button
                        onClick={() => cancelAction(i)}
                        disabled={m.actionState === "running"}
                        style={{
                          padding: "9px 14px", borderRadius: 8, border: "1px solid #d1d5db",
                          background: "#fff", color: "#374151", cursor: "pointer", fontSize: 14, fontWeight: 500,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {m.actionResult && (
                  <div
                    style={{
                      marginTop: 6, fontSize: 13, fontWeight: 500,
                      color: m.actionState === "error" ? "#dc2626" : m.actionState === "cancelled" ? "#6b7280" : "#059669",
                    }}
                  >
                    {m.actionResult}
                  </div>
                )}
              </div>
            ))}
            {sending && <div style={{ fontSize: 13, color: "#6b7280" }}>Assistant is typing…</div>}
          </div>

          <div style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid #e5e7eb" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your message…"
              style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#0066cc", color: "#fff", cursor: "pointer" }}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChatBubble;
